import { Context } from '../../server-middleware/context'
import { h, Component } from 'preact'
import MenuMain from '../menu/main.sync'
import { z } from 'zod'
import Search from '../algolia/components/Search'
import { CurrentUserContext } from '../../server-middleware/userContext'
import Phrase from '../../libs/translationsUtils/phrase'
import ClientComponent from '../client-component/index.server'
import { getCategories } from '../../server-middleware/categoryRepository'
import { CategoryInfo } from '../header-navigation/Types'
import getConfig from '../../services/configCommunity'
import { GeneralDbConfig } from 'js/src/services/generalConfig'
import UserFlag from '../User/Flag'
import Dropdown from '../User/Dropdown'

interface Props {
  categoryGroups: CategoryInfo[]
  knowledgeBaseCategories: CategoryInfo[]
  ssoLoginUrl: string
  isFederatedSalesforceSearch: boolean
  isFederatedFreshdeskSearch: boolean
  isFederatedSkilljarSearch: boolean
}

export const widgetName = 'navigation_bar'

export const config = z.undefined()

function isCategoryInfo (c: CategoryInfo|undefined): c is CategoryInfo {
  return !!c
}

function getCategoryId () {
  if (typeof window === 'undefined') return null
  return window.inSidedData.content.category.id
}

function isTopicNew () {
  if (typeof window === 'undefined') return null
  return window.location.pathname.match(/^\/topic\/new/)
}

function getContentType () {
  if (typeof window === 'undefined') return undefined
  if (window.location.pathname.match(/^\/ideas/)) {
    return ['ideation']
  }
  if (window.location.pathname.match(/^\/product-updates/)) {
    return ['productUpdate']
  }
  return undefined
}

export function prepCategories (forums: Array<{
  forumid: number
  title: string
  type: number|null
  displayorder: number
  threadcount: number
  forum: null|{
    forumid: number
    title: string
    type: number|null
    displayorder: number
    threadcount: number
  }
}>) {
  const tree = forums.reduce<Record<string, CategoryInfo|undefined>>((idMap, category) => {
    const parentCategory = category.forum
    if (!parentCategory) return idMap

    let parent = idMap[parentCategory.forumid]
    if (!parent) {
      parent = idMap[parentCategory.forumid] = {
        id: `${parentCategory.forumid}`,
        title: parentCategory.title,
        type: parentCategory.type || 0,
        displayOrder: parentCategory.displayorder,
        url: `${parentCategory.title.toLowerCase().replace(' ', '-')}-${parentCategory.forumid}`,
        topicCount: parentCategory.threadcount,
        subforums: []
      }
    }
    if (!parent?.subforums) return idMap

    parent.subforums.push({
      id: `${category.forumid}`,
      title: category.title,
      type: category.type || 0,
      displayOrder: category.displayorder,
      url: `${category.title.toLowerCase().replace(' ', '-')}-${category.forumid}`,
      topicCount: category.threadcount
    })
    return idMap
  }, {})

  return Object.keys(tree).map(id => tree[id]).filter(isCategoryInfo).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
}

export function decodeSsoUrl (c: GeneralDbConfig['config']['params']): string {
  // taken from src/InSided/Twig/Extension/BrandedExtension.php

  const ssoConfig = c.sso
  if (c.memberLogin?.active) return ''
  if (!ssoConfig?.active) return ''
  if (ssoConfig.proxy) return ssoConfig.login_url || ''

  if (ssoConfig?.twitter?.active) return ''
  if (ssoConfig?.facebook?.active) return ''
  if (ssoConfig?.linkedin?.active) return ''
  if (ssoConfig?.google?.active) return ''

  const customerSsoTypes = [
    'saml',
    'oracle',
    'token',
    'oauth_nos',
    'oauth_soundcloud',
    'oauth_tuenti',
    'openidconnect',
    'oauth2'
  ] as const

  for (const type of customerSsoTypes) {
    if (ssoConfig[type]?.active) return 'sso/login?ssoType=' + type
  }
  return ''
}

export const getServerSideProps = async (context: Context, _props: z.infer<typeof config>) => {
  const list = prepCategories(await getCategories(context))
  const [isFederatedSalesforceSearch] = (await Promise.allSettled([
    getConfig(context, 'salesforceFederatedSearchEnabled')
  ])).map((result) => {
    if (result.status === 'fulfilled') return result.value || false
    return false
  })

  return {
    categoryGroups: list.filter(c => c.type !== 1),
    knowledgeBaseCategories: list.filter(c => c.type === 1),
    isFederatedSalesforceSearch: isFederatedSalesforceSearch,
    isFederatedFreshdeskSearch: context.config.config.params.freshdesk?.enabledFederatedSearch || false,
    isFederatedSkilljarSearch: context.config.config.params.skilljar?.enabledFederatedSearch || false,
    ssoLoginUrl: decodeSsoUrl(context.config.config.params)
  }
}

class NavigationBar extends Component<Props> {
  render (props: Props) {
    return (
      <CurrentUserContext.Consumer>{user => {
        return (
          <nav class='main-navigation--wrapper qa-main-navigation custom-navigation'>
            <div class='main-navigation-sitewidth'>

              {ClientComponent(MenuMain, 'menu/main', {
                communityCategories: props.categoryGroups,
                knowledgeBaseCategories: props.knowledgeBaseCategories,
                showActivitySinceLastVisit: !!user.id
              })}

              <section class='main-navigation--search-wrapper is-hidden-mobile-only'>
                <form method='get' action='/search/index'>
                  <div class='searchbox searchbox--large qa-search-area'>
                    <div class='searchbox__input algolia-search--forum qa-menu-search-input'>
                      {ClientComponent(Search, 'algolia/components/Search', {
                        isUserGuest: !user.id,
                        isFederatedSalesforceSearch: props.isFederatedSalesforceSearch,
                        isFederatedSkilljarSearch: props.isFederatedSkilljarSearch,
                        isFederatedFreshdeskSearch: props.isFederatedFreshdeskSearch,
                        category: getCategoryId() || undefined,
                        isParentCategory: false, // TODO implement this and isParentCategory when extending this feature to category page
                        searchByContentType: getContentType(),
                        displayFilters: false
                      })}
                    </div>
                  </div>
                </form>
              </section>

              <section class='main-navigation--nav-buttons-wrapper js-main-navigation' data-view='MainNavigation'>
                <ul>
                  {!isTopicNew()
                    ? <li class='is-hidden-S'>
                      <a
                        href='/topic/new'
                        data-track={JSON.stringify({ trigger: 'navigation', type: 'Topic Initiated' })}
                        data-ga-track={JSON.stringify({ eventCategory: 'Homepage', eventAction: 'Create topic clicked', eventLabel: { Position: 'Navigation' } })}
                        class='link menu-create-topic qa-menu-create-topic'
                        tabIndex={0}
                        role='button'
                      >
                        <span class='icon--pen icon--auto-width' aria-hidden='true' />
                        <span><Phrase p='main.navigation.add_topic' /></span>
                      </a>
                    </li>
                    : null}

                  {!user.id
                    ? <li>
                      {props.ssoLoginUrl
                        ? <a href={props.ssoLoginUrl} tabIndex={0} role='button' class='link qa-header-login-button'><Phrase m='Common' p='main.navigation.login' /></a>
                        : <a href='#' class='link js-open-modal qa-header-login-button' tabIndex={0} role='button' data-modal='login'><Phrase m='Common' p='main.navigation.login' /></a>}
                    </li>
                    : <li class='main-navigation--profile-link qa-header-profile-link'>
                      <UserFlag {...user} />
                      <Dropdown {...user} />
                    </li>}
                </ul>
              </section>
            </div>

          </nav>
        )
      }}
      </CurrentUserContext.Consumer>
    )
  }
}

export default NavigationBar
